// Copyright (c) StrangeBee 2023

import React, { ReactElement } from 'react';
import { Input } from 'antd';
import { TaskInput, TaskOutput, UserOutput } from 'thehive-sdk';
import { normalizeDate, useLocalMessage } from '@Helpers/';
import { FormInstance } from 'antd/lib/form';
import AssigneeInfo from '@Molecules/AssigneeInfo/AssigneeInfo';
import { FormSubmitButton, FormSubmitOptions } from '@Atoms/Buttons/FormSubmitButton/FormSubmitButton';
import { THForm } from '@Molecules/THForm/THForm';
import { THInput } from '@Molecules/Inputs/THInput/THInput';
import { THAutoComplete } from '@Molecules/Inputs/THAutoComplete/THAutoComplete';
import { THMarkdown } from '@Molecules/Inputs/THMarkdown/THMarkdown';
import { THSwitch } from '@Molecules/Inputs/THSwitch/THSwitch';
import { THDatePicker } from '@Molecules/Inputs/THDatePicker/THDatePicker';
import { THSelect } from '@Molecules/Inputs/THSelect/THSelect';

export interface TaskValuesForm extends FormSubmitOptions {
    group?: string;
    title: string;
    description?: string;
    assignee?: string;
    flag?: boolean;
    dueDate?: moment.Moment;
    mandatory?: boolean;
}

interface TaskFormProps {
    form: FormInstance;
    defaultValues?: TaskInput | TaskOutput;
    loading: boolean;
    isValid: boolean;
    onFinish: (values: TaskValuesForm) => void;
    onChange: () => void;
    onCancel?: () => void;
    assignableUsers?: UserOutput[];
    allTaskList?: TaskOutput[];
    needDueDate?: boolean;
}

export const TaskForm = ({
    form,
    defaultValues,
    loading,
    isValid,
    onFinish,
    onChange,
    onCancel,
    assignableUsers = [],
    allTaskList = [],
    needDueDate = true,
}: TaskFormProps): ReactElement => {
    const i = useLocalMessage();

    const groupList = allTaskList?.reduce(
        (acc, { group }) => (group && !acc.includes(group) ? [...acc, group] : acc),
        [] as string[],
    );

    return (
        <THForm<TaskValuesForm>
            name="task-form"
            onFinish={onFinish}
            initialValues={{ group: 'default', ...defaultValues }}
            form={form}
            onValuesChange={onChange}
        >
            <THForm.Layout
                footer={
                    <FormSubmitButton
                        onCancel={onCancel}
                        {...(defaultValues
                            ? {}
                            : {
                                  onAnother: () => {
                                      form.setFieldsValue({
                                          another: true,
                                      });
                                      form.submit();
                                  },
                              })}
                        disabled={!isValid || loading}
                        formName="task-form"
                        loading={loading}
                    />
                }
            >
                <THForm.Item
                    name="group"
                    label={i('task.create.form.group.label')}
                    rules={[{ required: true, message: i('task.create.form.title.required') }]}
                >
                    <THAutoComplete
                        options={groupList?.map((group) => ({ label: group, value: group }))}
                        placeholder={i('task.create.form.group.placeholder')}
                    />
                </THForm.Item>

                <THForm.Item
                    name="title"
                    rules={[{ required: true, message: i('task.create.form.title.required') }]}
                    label={i('task.create.form.title.label')}
                >
                    <THInput placeholder={i('task.create.form.title.placeholder')} />
                </THForm.Item>

                <THForm.Item
                    name="mandatory"
                    label={i('task.create.form.mandatory.label')}
                    valuePropName="checked"
                    help={i('task.create.form.mandatory.help')}
                >
                    <THSwitch />
                </THForm.Item>

                <THForm.Item name="description" label={i('task.create.form.description.label')}>
                    <THMarkdown sameSize />
                </THForm.Item>

                {!!assignableUsers.length && (
                    <THForm.Item name="assignee" label={i('task.create.form.assignee.label')}>
                        <THSelect allowClear showSearch placeholder={i('task.create.form.assignee.placeholder')}>
                            {assignableUsers?.map(
                                ({ login, locked }) =>
                                    !locked && (
                                        <THSelect.Option key={login} value={login}>
                                            <AssigneeInfo assignee={login} />
                                        </THSelect.Option>
                                    ),
                            )}
                        </THSelect>
                    </THForm.Item>
                )}

                <THForm.Item name="flag" label={i('task.create.form.flag.label')} valuePropName="checked">
                    <THSwitch />
                </THForm.Item>

                <THForm.Item name="order" noStyle>
                    <Input type="hidden" />
                </THForm.Item>

                {needDueDate && (
                    <THForm.Item label={i('task.create.form.due-date.label')} name="dueDate" normalize={normalizeDate}>
                        <THDatePicker />
                    </THForm.Item>
                )}
            </THForm.Layout>
        </THForm>
    );
};
